import React, { Ref, useContext } from 'react';

import cx from 'classnames';
import { has } from 'ramda';
import { Element } from 'react-scroll';

import { LANDING_PAGE_TYPES } from 'constants/index';
import { useCustomStyles, useCustomFonts } from 'hooks/useCustomFront';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useWebViewLinksHandler } from 'hooks/useWebViewLinksHandler';
import { LANDING_THEMES } from 'utils/itineraries/functions';

import { getPageTypesConfig } from '../../Pages/Admin/Page/Landing/pageTypesConfig';
import LandingPageContext from './contexts/LandingPageContext';
import LandingPageTypeContext from './contexts/LandingPageTypeContext';
import { useBrandingStyles } from './functions';
import s from './index.module.css';
import LeftSide from './LeftSide';
import RightSide from './RightSide';
import { IWrapperProps, ILandingPageProps } from './types';
import { getClassNameByLandingPageType } from './utils';

function Wrapper({ children, anchor, className }: IWrapperProps) {
  if (!anchor) {
    return <div className={cx(s.landingWrapper, 'custom-landing-wrapper', className)}>{children}</div>;
  }

  return (
    <Element name={anchor} id={anchor} className={cx(s.landingWrapper, 'landing', 'custom-landing-wrapper', className)}>
      {children}
    </Element>
  );
}

function LandingPage({
  initialAnchorSection,
  landingPageType,
  className,
  anchor,
  renderStartArrow,
  index = 0,
  headlineHint,
}: ILandingPageProps) {
  useBrandingStyles();
  useWebViewLinksHandler();
  const { pageTypeContextProps } = getPageTypesConfig(landingPageType);
  const { isTabletOrMobile } = useMediaBreakpoints();
  const { landingData, isItinerariesItem, landingTheme, virtualized } = useContext(LandingPageContext);
  const originalId = has('originalId', landingData) ? landingData.originalId : null;
  const id = has('id', landingData) ? landingData.id : null;
  const customId = !isItinerariesItem ? id : originalId || id;
  useCustomStyles({ css: landingData.customStyles, customId });
  useCustomFonts({ landingItem: landingData, customId });
  const hasLeftSideOnTopLevel =
    (!isTabletOrMobile && landingTheme === LANDING_THEMES.default) ||
    (!isTabletOrMobile && landingTheme === LANDING_THEMES.boats && landingPageType !== LANDING_PAGE_TYPES.COVER_PAGE);
  const hasLeftSideInsideWrapper =
    (!isTabletOrMobile && landingTheme !== LANDING_THEMES.default && landingTheme !== LANDING_THEMES.boats) ||
    (!isTabletOrMobile && landingTheme === LANDING_THEMES.boats && landingPageType === LANDING_PAGE_TYPES.COVER_PAGE);

  return (
    // @ts-ignore
    <LandingPageTypeContext.Provider value={{ ...pageTypeContextProps, headlineHint }}>
      {hasLeftSideOnTopLevel && <LeftSide blockId={`p-${customId}`} index={index} />}

      <Wrapper anchor={anchor} className={`custom-landing-wrapper-${landingPageType}`}>
        {/* LeftSide has to be inside Wrapper within nomad theme! Otherwise sticky Primary images stretched to 100% will conflict with navigation scroll */}
        {hasLeftSideInsideWrapper && <LeftSide blockId={`p-${customId}`} index={index} />}
        <div
          className={cx(s.landing, className, 'custom-landing', getClassNameByLandingPageType(landingPageType))}
          id={`p-${customId}`}
        >
          {isTabletOrMobile && <LeftSide blockId={`p-${customId}`} index={index} />}
          <RightSide
            ref={virtualized?.measureElement as Ref<HTMLDivElement>}
            initialAnchorSection={initialAnchorSection}
            renderStartArrow={renderStartArrow}
          />
        </div>
      </Wrapper>
    </LandingPageTypeContext.Provider>
  );
}

export default LandingPage;
